// ============================================================================
// BP-09-04: Forum Hooks
// BrightPath Community - Forums React Query Hooks
// ============================================================================

import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
  UseQueryOptions,
} from '@tanstack/react-query';
import { forumApi } from '../api';
import {
  ForumCategory,
  ForumPost,
  ForumPostListItem,
  ForumPostDetailResponse,
  ForumPostsQueryParams,
  ForumSearchParams,
  CreateForumPostInput,
  UpdateForumPostInput,
  CreateForumReplyInput,
} from '../types';

// =============================================================================
// Query Keys
// =============================================================================

export const forumKeys = {
  all: ['forums'] as const,
  categories: () => [...forumKeys.all, 'categories'] as const,
  category: (slug: string) => [...forumKeys.categories(), slug] as const,
  posts: () => [...forumKeys.all, 'posts'] as const,
  postList: (params?: ForumPostsQueryParams) => [...forumKeys.posts(), params] as const,
  categoryPosts: (slug: string, params?: ForumPostsQueryParams) => 
    [...forumKeys.posts(), 'category', slug, params] as const,
  post: (id: string) => [...forumKeys.posts(), id] as const,
  search: (params: ForumSearchParams) => [...forumKeys.all, 'search', params] as const,
  recentActivity: () => [...forumKeys.all, 'recent'] as const,
  tag: (tag: string) => [...forumKeys.all, 'tag', tag] as const,
};

// =============================================================================
// Category Hooks
// =============================================================================

export function useForumCategories(
  options?: Omit<UseQueryOptions<{ categories: ForumCategory[] }>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: forumKeys.categories(),
    queryFn: () => forumApi.getCategories(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
}

export function useForumCategory(
  slug: string,
  options?: Omit<UseQueryOptions<ForumCategory>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: forumKeys.category(slug),
    queryFn: () => forumApi.getCategoryBySlug(slug),
    enabled: !!slug,
    ...options,
  });
}

// =============================================================================
// Post Hooks
// =============================================================================

export function useCategoryPosts(
  categorySlug: string,
  params?: Omit<ForumPostsQueryParams, 'category_slug'>
) {
  return useQuery({
    queryKey: forumKeys.categoryPosts(categorySlug, params),
    queryFn: () => forumApi.getCategoryPosts(categorySlug, params),
    enabled: !!categorySlug,
  });
}

export function useForumPosts(params?: ForumPostsQueryParams) {
  return useQuery({
    queryKey: forumKeys.postList(params),
    queryFn: () => forumApi.getAllPosts(params),
  });
}

export function useInfiniteForumPosts(
  categorySlug?: string,
  params?: Omit<ForumPostsQueryParams, 'page'>
) {
  return useInfiniteQuery({
    queryKey: categorySlug 
      ? forumKeys.categoryPosts(categorySlug, params)
      : forumKeys.postList(params),
    queryFn: ({ pageParam = 1 }) => 
      categorySlug
        ? forumApi.getCategoryPosts(categorySlug, { ...params, page: pageParam })
        : forumApi.getAllPosts({ ...params, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (lastPage.page < lastPage.total_pages) {
        return lastPage.page + 1;
      }
      return undefined;
    },
  });
}

export function useForumPost(postId: string) {
  return useQuery({
    queryKey: forumKeys.post(postId),
    queryFn: () => forumApi.getPost(postId),
    enabled: !!postId,
  });
}

export function useForumSearch(params: ForumSearchParams) {
  return useQuery({
    queryKey: forumKeys.search(params),
    queryFn: () => forumApi.searchPosts(params),
    enabled: !!params.query && params.query.length >= 2,
  });
}

export function useRecentActivity() {
  return useQuery({
    queryKey: forumKeys.recentActivity(),
    queryFn: () => forumApi.getRecentActivity(),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

// =============================================================================
// Post Mutations
// =============================================================================

export function useCreatePost(categorySlug: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateForumPostInput) => forumApi.createPost(categorySlug, data),
    onSuccess: (newPost) => {
      // Invalidate category posts
      queryClient.invalidateQueries({ queryKey: forumKeys.categoryPosts(categorySlug) });
      // Invalidate all posts
      queryClient.invalidateQueries({ queryKey: forumKeys.posts() });
      // Invalidate recent activity
      queryClient.invalidateQueries({ queryKey: forumKeys.recentActivity() });
      // Invalidate category to update post count
      queryClient.invalidateQueries({ queryKey: forumKeys.categories() });
    },
  });
}

export function useUpdatePost(postId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateForumPostInput) => forumApi.updatePost(postId, data),
    onSuccess: (updatedPost) => {
      // Update the post in cache
      queryClient.setQueryData(forumKeys.post(postId), (old: ForumPostDetailResponse) => ({
        ...old,
        post: updatedPost,
      }));
      // Invalidate post lists
      queryClient.invalidateQueries({ queryKey: forumKeys.posts() });
    },
  });
}

export function useDeletePost() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (postId: string) => forumApi.deletePost(postId),
    onSuccess: (_, postId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: forumKeys.post(postId) });
      // Invalidate post lists
      queryClient.invalidateQueries({ queryKey: forumKeys.posts() });
      // Invalidate categories
      queryClient.invalidateQueries({ queryKey: forumKeys.categories() });
    },
  });
}

export function useTogglePostLike(postId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => forumApi.togglePostLike(postId),
    onMutate: async () => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: forumKeys.post(postId) });

      // Snapshot previous value
      const previous = queryClient.getQueryData<ForumPostDetailResponse>(forumKeys.post(postId));

      // Optimistically update
      if (previous) {
        queryClient.setQueryData(forumKeys.post(postId), {
          ...previous,
          post: {
            ...previous.post,
            is_liked_by_user: !previous.post.is_liked_by_user,
            like_count: previous.post.is_liked_by_user 
              ? previous.post.like_count - 1 
              : previous.post.like_count + 1,
          },
        });
      }

      return { previous };
    },
    onError: (err, _, context) => {
      // Rollback on error
      if (context?.previous) {
        queryClient.setQueryData(forumKeys.post(postId), context.previous);
      }
    },
    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: forumKeys.post(postId) });
    },
  });
}

// =============================================================================
// Reply Mutations
// =============================================================================

export function useCreateReply(postId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateForumReplyInput) => forumApi.createReply(postId, data),
    onSuccess: () => {
      // Invalidate post to get new replies
      queryClient.invalidateQueries({ queryKey: forumKeys.post(postId) });
      // Invalidate posts list to update reply count
      queryClient.invalidateQueries({ queryKey: forumKeys.posts() });
    },
  });
}

export function useUpdateReply(postId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ replyId, content }: { replyId: string; content: string }) =>
      forumApi.updateReply(replyId, content),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: forumKeys.post(postId) });
    },
  });
}

export function useDeleteReply(postId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (replyId: string) => forumApi.deleteReply(replyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: forumKeys.post(postId) });
      queryClient.invalidateQueries({ queryKey: forumKeys.posts() });
    },
  });
}

export function useToggleReplyLike(postId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (replyId: string) => forumApi.toggleReplyLike(replyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: forumKeys.post(postId) });
    },
  });
}

export function useMarkAsSolution(postId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (replyId: string) => forumApi.markAsSolution(replyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: forumKeys.post(postId) });
    },
  });
}
